import { IconChevronDown, IconChevronRight, } from '@tabler/icons-react';
import { Box, Menu, NavLink, ScrollArea, } from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
// import CustomDivider from '../../../components/ui/CustomDivider/CustomDivider';
import DisplayIcon from '../../../components/iconSelector/DispalyIcon';
import { LayoutNavbarWithSkeleton } from '../../../components/SkeletonLoading';
import { navBarTextColor } from '../../../components/mantineReactTable/sizes';
import { buildRoutePath, findRegisteredRoute, ROUTES } from '../../../routes/paths';
import '../Layout.css'
// Interfaces
export interface NavbarProps {
  isSkeleton?: boolean;
  toggleMobile: () => void;
  desktopOpened?: boolean;
  mobileOpened?: boolean;
  stopNavigation?: boolean;
  activeLink?: string;
  menuItems: NavItem[];
  isMenuLoading: boolean;
}


export interface NavItem {
  navMenuName: string;
  navMenuUrl: string;
  iconName: string;
  iconColor: string;
  id?: string;
  parentMenuId: string;
  isPageConfigured?: boolean;
  isActive?: boolean;
  isQuickCreate?: boolean;
  quickCreateName?: string;
  displayOrder?: number;
  menuGroupType: string;
  isInternalPath?: boolean;
  childMenus?: NavItem[];
}

const hasAvailableRoute = (item: NavItem): boolean =>
  item.isInternalPath !== true &&
  ((item.isActive === true && Boolean(findRegisteredRoute(item.navMenuUrl))) ||
    item.childMenus?.some(hasAvailableRoute) === true);

const getAvailableRoute = (item: NavItem): string | undefined => {
  const activeChildRoute = item.childMenus
    ?.filter(hasAvailableRoute)
    .map(getAvailableRoute)
    .find((url): url is string => Boolean(url));

  return activeChildRoute ?? (
    item.isInternalPath !== true &&
      item.isActive === true &&
      findRegisteredRoute(item.navMenuUrl)
      ? item.navMenuUrl
      : undefined
  );
};

const Navbar = ({toggleMobile, desktopOpened, stopNavigation, activeLink, menuItems, isMenuLoading }: NavbarProps) => {
  const location = useLocation();
  const navRefs = useRef<Record<string, HTMLDivElement | null>>({});  // const [openIndex, setOpenIndex] = useState<number | null>(null);
  const locationState = location.state;
  const activeTab = typeof locationState === 'object' && locationState !== null && 'activeTab' in locationState &&
    typeof locationState.activeTab === 'string' ? locationState.activeTab : 'Active';
  const isAddingTeamMember = location.pathname.replace(/\/+$/, '').toLowerCase() === ROUTES.people.addTeamMembers.replace(/\/+$/, '').toLowerCase();
  const currentPath = activeLink ?? (isAddingTeamMember ? buildRoutePath(ROUTES.people.teamMemberStatus, { status: activeTab }) : location.pathname);
  
  const HIGHLIGHT_COLOR = 'rgb(73 212 228)';
  const PARENT_COLOR = navBarTextColor;
  const CHILD_NAV_COLOR = PARENT_COLOR;
  const ACTIVE_FONT_WEIGHT = 600;
  const INACTIVE_FONT_WEIGHT = 600;
  const ioncStrokeWidth = 1.8
  const iconWidth = '1.2rem'
  const iconHeight = '1.2rem'
  // Utility Functions
  const isActive = (url: string): boolean => {
    return matchPath(
      { path: url.startsWith('/') ? url : `/${url}`, end: false, caseSensitive: false },
      currentPath,
    ) !== null;
  }

  const isChildActive = (_parentUrl: string, childUrl: string): boolean => {
    return isActive(childUrl);
  };

  const hasActiveChild = (children?: NavItem[]): boolean => {
    return children?.some(child =>
      (hasAvailableRoute(child) && child.isActive === true && isActive(child.navMenuUrl)) ||
      hasActiveChild(child.childMenus)
    ) ?? false;
  };

  const renderIcon = (iconName: string, color: string, tooltipLabel: string = '',) => {
    return desktopOpened ? (
      <DisplayIcon iconName={iconName} iconColor={color} strokeWidth={ioncStrokeWidth} width={iconWidth} height={iconHeight} />
    ) : <DisplayIcon iconName={iconName} strokeWidth={ioncStrokeWidth} width={iconWidth} height={iconHeight} iconColor={color} isHover={true} toolTipLabel={tooltipLabel} />;
  };
  useEffect(() => {
    const pathSegments = currentPath.split('/');

    let activeParentId: string | null = null;

    menuItems?.forEach((item: NavItem) => {
      if (pathSegments[1] === item?.navMenuUrl?.split('/')[1]) {
        activeParentId = item.id || null;
      }

      item.childMenus?.forEach((child) => {
        if (pathSegments[2] === child.navMenuUrl.split('/')[2]) {
          activeParentId = item.id || null;
        }
      });
    });

    setOpenMenuId(activeParentId);

    if (activeParentId && navRefs.current[activeParentId]) {
      navRefs.current[activeParentId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentPath, menuItems]);


  const groupedMenus = useMemo(() => {
    const groups: Record<string, NavItem[]> = {};
    menuItems.filter(hasAvailableRoute).forEach((item) => {
      const module = item.menuGroupType || "";
      if (!groups[module]) {
        groups[module] = [];
      }
      groups[module].push(item);
    });
    return groups;
  }, [menuItems]);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  return (
    <>
      {(isMenuLoading) ? (<LayoutNavbarWithSkeleton />) : (
        <ScrollArea scrollbarSize={1} scrollHideDelay={0} scrollbars={'y'}>
          <Box mt={1} >

            {Object.entries(groupedMenus).map(([module, menuItems]) => (
              <Box key={module}>
                {desktopOpened && (
                  <Box
                    ml={12}
                    pt={10}
                    pb={4}
                    style={{
                      fontSize: 10,
                      fontWeight: 900,
                      color: "rgba(100, 116, 139, 1)",
                      textTransform: "uppercase",
                    }}
                  >
                    {module}
                  </Box>
                )}

                {menuItems.filter(hasAvailableRoute).map((item, index) => {
                  const availableChildren = item.childMenus?.filter(hasAvailableRoute) ?? [];
                  const itemRoute = getAvailableRoute(item) ?? item.navMenuUrl;
                  // const globalIndex = groupIndex * 100 + index;
                  const isOpen =
                    (!stopNavigation && openMenuId === item.id) ||
                    hasActiveChild(item.childMenus);

                  const isOpenWithoutStyle =
                    stopNavigation && openMenuId === item.id;

                  const isParentActive =
                    (item.isActive === true && isActive(item.navMenuUrl)) ||
                    hasActiveChild(item.childMenus);

                  const ChevronIcon =
                    isOpenWithoutStyle || isOpen ? IconChevronDown : IconChevronRight;

                  const iconColor = isParentActive
                    ? HIGHLIGHT_COLOR
                    : item.iconColor;

                  return (
                    <Box
                      key={item.id || index}
                      ref={(el) => {
                        if (!stopNavigation && item.id) {
                          navRefs.current[item.id] = el;
                        }
                      }}
                    >
                      {desktopOpened ? (
                        <>
                          <NavLink
                            label={item?.navMenuName}
                            c={isParentActive ? HIGHLIGHT_COLOR : PARENT_COLOR}
                            fw={
                              isParentActive
                                ? ACTIVE_FONT_WEIGHT
                                : INACTIVE_FONT_WEIGHT
                            }
                            active={isParentActive}
                            childrenOffset={16}
                            component={Link}
                            to={itemRoute}
                            onClick={(e) => {
                              if (stopNavigation) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                              setOpenMenuId(item.id || null)
                              toggleMobile();
                            }}
                            leftSection={renderIcon(item?.iconName, iconColor)}
                            rightSection={
                              availableChildren.length ? (
                                <ChevronIcon size={16} />
                              ) : null
                            }
                            classNames={{
                              root: "custom-navlink-root",
                            }}
                          />

                          {(isOpenWithoutStyle || isOpen) &&
                            availableChildren.map((child, childIndex) => {
                              const active =
                                isActive(child.navMenuUrl) ||
                                isChildActive(
                                  item.navMenuUrl,
                                  child.navMenuUrl
                                );

                              return (
                                <NavLink
                                  key={child?.id || childIndex}
                                  ml={38}
                                  mt={1}
                                  fz={"xs"}
                                  classNames={{
                                    root: "custom-navlink-root",
                                  }}
                                  c={
                                    active
                                      ? HIGHLIGHT_COLOR
                                      : CHILD_NAV_COLOR
                                  }
                                  active={active}
                                  label={child?.navMenuName}
                                  component={Link}
                                  to={getAvailableRoute(child) ?? child.navMenuUrl}
                                  onClick={(e) => {
                                    if (stopNavigation) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }
                                    setOpenMenuId(item.id || null)
                                    toggleMobile();
                                  }}
                                />
                              );
                            })}
                        </>
                      ) : (
                        <Box
                          mt={15}
                          mb={availableChildren.length === 0 ? -8 : 0}
                        >
                          {availableChildren.length ? (
                            <Menu
                              trigger="hover"
                              position="right"
                              offset={-13}
                              withArrow
                              arrowSize={12}
                            >
                              <Menu.Target>
                                <Box
                                  component={Link}
                                  to={itemRoute}
                                  onClick={() => {
                                    setOpenMenuId(item.id || null);
                                    toggleMobile();
                                  }}
                                  style={{ cursor: "pointer" }}
                                  pt={8}
                                  pl={10}
                                >
                                  {renderIcon(item?.iconName, iconColor)}
                                </Box>
                              </Menu.Target>

                              <Menu.Dropdown>
                                <Menu.Item
                                  key="parent"
                                  component={Link}
                                  to={itemRoute}
                                  onClick={() => {
                                    setOpenMenuId(item.id || null)
                                  }}
                                  fz={"xs"}
                                  leftSection={renderIcon(
                                    item?.iconName,
                                    item?.iconColor,
                                    item?.navMenuName
                                  )}
                                >
                                  <strong>{item.navMenuName}</strong>
                                </Menu.Item>

                                {availableChildren.map((child, childIdx) => {
                                  const childIsActive = isActive(
                                    child.navMenuUrl
                                  );

                                  return (
                                    <Menu.Item
                                      key={child?.id || childIdx}
                                      component={Link}
                                      to={getAvailableRoute(child) ?? child.navMenuUrl}
                                      fz={"xs"}
                                      ml={12}
                                      onClick={() => {
                                        setOpenMenuId(item.id || null)
                                      }}
                                      style={{
                                        fontWeight: childIsActive
                                          ? ACTIVE_FONT_WEIGHT
                                          : INACTIVE_FONT_WEIGHT,
                                        color: childIsActive
                                          ? HIGHLIGHT_COLOR
                                          : undefined,
                                      }}
                                      leftSection={
                                        <DisplayIcon
                                          iconName={child?.iconName}
                                          strokeWidth={ioncStrokeWidth}
                                          width={iconWidth}
                                          height={iconHeight}
                                          iconColor={
                                            childIsActive
                                              ? HIGHLIGHT_COLOR
                                              : child.iconColor
                                          }
                                          isHover={true}
                                          toolTipLabel={child.navMenuName}
                                        />
                                      }
                                    >
                                      {child?.navMenuName}
                                    </Menu.Item>
                                  );
                                })}
                              </Menu.Dropdown>
                            </Menu>
                          ) : (
                            <Box
                              component={Link}
                              to={itemRoute}
                              onClick={() => {
                                setOpenMenuId(item.id || null)
                              }}
                              pl={10}
                              pt={index === 0 ? 0 : 6}
                              data-active={isParentActive || undefined}
                              style={{
                                display: "inline-block",
                                cursor: "pointer",
                              }}
                            >
                              {renderIcon(
                                item?.iconName,
                                isParentActive
                                  ? HIGHLIGHT_COLOR
                                  : item.iconColor,
                                item?.navMenuName
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </ScrollArea>
      )}
    </>
  );
};
export default Navbar;